import {
  BUSINESS_TIER,
  PERSONAL_TIER_ORDER,
  PRODUCT_TIERS,
  normalizeProductTier,
} from "./productCatalog";

export const MANUCALC_PLANS = PRODUCT_TIERS;

export const FEATURE_ACCESS = {
  calculators: {
    minimumPlan: "lite",
    label: "Calculations Suite",
    benefits: ["Core shop math", "Visible formulas", "Saved calculations"],
  },
  formulaReference: {
    minimumPlan: "lite",
    label: "Formula and material references",
    benefits: ["Formula notes", "Material data", "Source and assumption notes"],
  },
  manufacturingPlans: {
    minimumPlan: BUSINESS_TIER,
    requiredAccountType: "business",
    label: "Manufacturing Plans",
    benefits: ["Job/customer context", "Operation routing", "Cloud-saved plans"],
  },
  shopRecords: {
    minimumPlan: BUSINESS_TIER,
    requiredAccountType: "business",
    label: "Setup sheets, travelers, inspections, and shop records",
    benefits: ["Setup sheets", "Job travelers", "Inspection records", "Tool and machine records"],
  },
  collaboration: {
    minimumPlan: BUSINESS_TIER,
    requiredAccountType: "business",
    label: "Company messaging and collaboration",
    benefits: ["Company messages", "Notifications", "Shared work context"],
  },
  fileCenter: {
    minimumPlan: BUSINESS_TIER,
    requiredAccountType: "business",
    label: "Company workspace files",
    benefits: ["Plan attachments", "Cloud file metadata", "Open/download workflows"],
  },
  aiCopilot: {
    minimumPlan: "pro",
    label: "ManuCalc Assistant",
    benefits: ["Manufacturing Q&A", "Plan review", "Draft SOP/setup/inspection content"],
  },
  adminDashboard: {
    minimumPlan: BUSINESS_TIER,
    requiredAccountType: "business",
    label: "Business admin dashboard",
    benefits: ["Employee seats", "Company workspace", "Assigned work"],
  },
  advancedExports: {
    minimumPlan: "pro",
    label: "Advanced exports and backups",
    benefits: ["Personal workspace export", "Tool/job/project data export", "Calculation backups"],
  },
  companyExports: {
    minimumPlan: BUSINESS_TIER,
    requiredAccountType: "business",
    label: "Company exports and records",
    benefits: ["Company workspace export", "Business records export", "Audit and KPI exports"],
  },
};

export const PLAN_ORDER = PERSONAL_TIER_ORDER;

export const normalizePlanId = (value) => {
  const normalized = normalizeProductTier(value);
  if (normalized) return normalized;

  const specialAccess = String(value || "").toLowerCase();
  if ([
    "founder",
    "admin",
    "platform_owner",
    "platform-owner",
    "lifetime_admin",
    "lifetime-admin",
  ].includes(specialAccess)) {
    return BUSINESS_TIER;
  }

  return "";
};

export const planMeetsRequirement = (planId, minimumPlan = "lite") => {
  const normalizedPlan = normalizePlanId(planId) || "lite";
  const normalizedRequirement = normalizePlanId(minimumPlan) || "lite";

  if (normalizedRequirement === BUSINESS_TIER) {
    return normalizedPlan === BUSINESS_TIER;
  }

  if (normalizedPlan === BUSINESS_TIER) {
    return true;
  }

  const planIndex = PLAN_ORDER.indexOf(normalizedPlan);
  const requiredIndex = PLAN_ORDER.indexOf(normalizedRequirement);
  return planIndex >= requiredIndex;
};

export const getFeatureDefinition = (featureId) =>
  FEATURE_ACCESS[featureId] || { minimumPlan: "lite", label: featureId || "Feature" };

export const inferPlanId = ({
  accountProfile = {},
  subscriptionStatus = "",
  trialStatus = null,
} = {}) => {
  if (
    accountProfile?.platformOwner ||
    accountProfile?.lifetimeAdminGrant ||
    accountProfile?.accessSource === "lifetime_admin" ||
    accountProfile?.accountBillingType === "lifetimeAdmin" ||
    accountProfile?.isFounder ||
    accountProfile?.billingExempt ||
    accountProfile?.subscriptionRequired === false ||
    accountProfile?.accountBillingType === "founderLifetime"
  ) {
    return BUSINESS_TIER;
  }

  const companyPlan = normalizePlanId(
    accountProfile?.companyPlan ||
      accountProfile?.companyPlanId ||
      accountProfile?.businessPlan ||
      accountProfile?.businessPlanId
  );
  if (accountProfile?.accountBillingType === "companySeat" && companyPlan) {
    return companyPlan;
  }

  const explicitPlan = normalizePlanId(
    accountProfile?.planId ||
      accountProfile?.planTier ||
      accountProfile?.subscriptionTier ||
      accountProfile?.priceTier ||
      accountProfile?.productTier
  );
  if (explicitPlan) return explicitPlan;

  if (trialStatus?.isTrialActive) return "pro";

  if (["active", "trialing"].includes(String(subscriptionStatus || "").toLowerCase())) {
    return "lite";
  }

  return "lite";
};

export const getFeatureAccessDecision = ({
  feature,
  accountProfile = {},
  subscriptionStatus = "",
  trialStatus = null,
  hasBaseAccess = false,
} = {}) => {
  const definition = getFeatureDefinition(feature);
  const currentPlan = inferPlanId({ accountProfile, subscriptionStatus, trialStatus });
  const requiredAccountType = definition.requiredAccountType || "any";
  const isBusinessProfile =
    accountProfile?.platformOwner ||
    accountProfile?.lifetimeAdminGrant ||
    accountProfile?.accessSource === "lifetime_admin" ||
    accountProfile?.accountBillingType === "lifetimeAdmin" ||
    accountProfile?.isFounder ||
    accountProfile?.accountType === "business" ||
    accountProfile?.accountType === "company" ||
    accountProfile?.accountBillingType === "founderLifetime" ||
    accountProfile?.accountBillingType === "companySeat" ||
    Boolean(accountProfile?.companyId || accountProfile?.companyOwnerUid || accountProfile?.activeWorkspaceId);
  const isElevated =
    accountProfile?.platformOwner ||
    accountProfile?.lifetimeAdminGrant ||
    accountProfile?.accessSource === "lifetime_admin" ||
    accountProfile?.accountBillingType === "lifetimeAdmin" ||
    accountProfile?.isFounder ||
    accountProfile?.billingExempt ||
    accountProfile?.subscriptionRequired === false ||
    accountProfile?.accountBillingType === "founderLifetime";
  const meetsAccountRequirement =
    requiredAccountType === "any" ||
    (requiredAccountType === "business" && isBusinessProfile);

  const allowed =
    Boolean(hasBaseAccess) &&
    (isElevated ||
      (meetsAccountRequirement &&
        (trialStatus?.isTrialActive || planMeetsRequirement(currentPlan, definition.minimumPlan))));

  return {
    allowed,
    feature,
    definition,
    currentPlan,
    requiredPlan: definition.minimumPlan,
    currentPlanName: MANUCALC_PLANS[currentPlan]?.name || MANUCALC_PLANS.lite.name,
    requiredPlanName: MANUCALC_PLANS[definition.minimumPlan]?.name || MANUCALC_PLANS.lite.name,
    requiredAccountType,
    benefits: definition.benefits || [],
  };
};
